define((require) => {
    "use strict";

    var _                                       = require("underscore"),
        $                                       = require("jquery"),
        AbstractWidgetView                      = require("shared/ui/experience/widgets/AbstractWidgetView"),
        SalesforceCommerce                      = require("../service/SalesforceCommerceConnect"),
        SalesforceCommerceOCAPIServiceFactory   = require("../service/SalesforceCommerceOCAPIServiceFactory"),
        SalesforceCommerceProductModel          = require("../model/SalesforceCommerceProductModel"),
        SalesforceCommerceWidgetPreview         = require("ui/experience/widgets/salesforceCommerceWidget/view/SalesforceCommerceWidgetPreview"),
        Tooltip                                 = require("ui/common/Tooltip");

    /**
     * The view rendering an SalesforceCommerce widget.
     *
     * @class TransformableSalesforceCommerceWidgetView
     * @constructor
     *
     * @cfg {TransformableSalesforceCommerceWidgetView} The model of the SalesforceCommerce widget being rendered.
     */
    return AbstractWidgetView.extend("TransformableSalesforceCommerceWidgetView", {

        tagName: "div",

        className: AbstractWidgetView.prototype.className + " salesforce-commerce-widget",

        _$salesforceCommerceElement: null,

        /**
         * Last SalesforceCommerce product model selected.
         */
        _lastSalesforceCommerceProductModel: null,

        /**
         * The last state of this widget instance.
         */
        _lastIsSmallWidget: false,

        updateRendering: function(element, bounds, active, edited, changedProperties) {
            if (element) {
                // Render the SalesforceCommerce container
                if (changedProperties && !_.isUndefined(changedProperties.productID)) {
                    this._renderProduct(element, bounds, changedProperties.productID, changedProperties.siteId);
                } else {
                    var productID = this.model.get("productID");
                    if (!this._$salesforceCommerceElement) {
                        if (!_.isNull(productID)) {
                            this._renderProduct(element, bounds, productID, this.model.get("siteId"));
                        } else {
                            this._renderEmptyState(element);
                        }
                    } else if (this._lastSalesforceCommerceProductModel) {
                        this._renderChangedSize(element, bounds, this._lastSalesforceCommerceProductModel);
                    }
                }
            }
        },

        /**
         * Render product when there isn't any product selected.
         *
         * @param {jQuery} element to be rendered.
         * @private
         */
        _renderEmptyState: function(element) {
            this._$salesforceCommerceElement = $("<div class='placeholder-icon'></div>");
            this._$salesforceCommerceElement.appendTo(element);
        },

        /**
         * Render image according with the size.
         *
         * @param {jQuery} element to be rendered.
         * @param {Boolean} isSmall
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel
         * @private
         */
        _renderAccordingToSize: function(element, isSmall, salesforceCommerceProductModel) {
            element.empty();
            if (isSmall) {
                this._renderSmallWidget(element, salesforceCommerceProductModel);
            } else {
                this._renderBigWidget(element, salesforceCommerceProductModel);
            }
        },

        /**
         * The size has changed, this function will be sure that we render the right state.
         *
         * @param {jQuery} element to be rendered.
         * @param {Object} bounds with width/height.
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel the current product.
         * @private
         */
        _renderChangedSize: function(element, bounds, salesforceCommerceProductModel) {
            if (this._lastIsSmallWidget !== this._isSmallWidget(bounds, salesforceCommerceProductModel)) {
                this._lastIsSmallWidget = !this._lastIsSmallWidget;
                this._renderAccordingToSize(element, this._lastIsSmallWidget, salesforceCommerceProductModel);
            }
        },

        /**
         * Render product according with its id and site id.
         *
         * @param {jQuery} element to be rendered.
         * @param {Object} bounds width/height of the product.
         * @param {Number} id to be rendered.
         * @param {String} siteObjectId the internal SalesforceCommerce siteId to search for (Mongo Object Id).
         * @private
         */
        _renderProduct: function(element, bounds, id, siteObjectId) {
            var self = this;

            var handlerProductBySize = function(salesforceCommerceProductModel) {
                self._lastSalesforceCommerceProductModel = salesforceCommerceProductModel;
                self._lastIsSmallWidget = self._isSmallWidget(bounds, salesforceCommerceProductModel);
                self._renderAccordingToSize(element, self._lastIsSmallWidget, salesforceCommerceProductModel);
            };

            var siteID = SalesforceCommerce.getSiteByObjectId(siteObjectId);
            SalesforceCommerceOCAPIServiceFactory.getService().getProductByIds(id, siteID).done(handlerProductBySize).fail(() => {
                handlerProductBySize(new SalesforceCommerceProductModel({id: id, status: "fail"}));
            });
        },

        /**
         * Check if the widget is small, usually meaning smaller than the product thumbnail image.
         *
         * @param {Object} bounds Bounds of widget, containing width and height.
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel The product shown on the widget.
         * @return {Boolean} True if a small widget, false if a big one.
         * @private
         */
        _isSmallWidget: function(bounds, salesforceCommerceProductModel) {
            var minimumSizeOfLargeWidget = 80;

            // If there is an issue with the product, the text in the box will be longer. We
            // increase the required size of the box, to avoid showing cropped text. The 130 pixels
            // have been determined empiricaly.
            if (salesforceCommerceProductModel.get("status") === "fail") {
                minimumSizeOfLargeWidget = 130;
            }

            return bounds.height < minimumSizeOfLargeWidget || bounds.width < minimumSizeOfLargeWidget;
        },

        /**
         * Render a small SalesforceCommerce icon with a tooltip.
         *
         * @param {jQuery} element to be rendered.
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel the current product.
         * @private
         */
        _renderSmallWidget: function(element, salesforceCommerceProductModel) {
            this._$salesforceCommerceElement = $("<div class='placeholder-icon'></div>");
            this._$salesforceCommerceElement.appendTo(element);

            var tooltipElement = this._createWidgetContainer(salesforceCommerceProductModel);
            Tooltip.add(this._$salesforceCommerceElement, {
                content: tooltipElement,
                width: 300,
                placement: "top",
                delay: {
                    show: 1000,
                    hide: 0
                }
            });
        },

        /**
         * Render the product in the big canvas.
         *
         * @param {jQuery} element to be rendered.
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel the current product.
         * @private
         */
        _renderBigWidget: function(element, salesforceCommerceProductModel) {
            this._$salesforceCommerceElement = this._createWidgetContainer(salesforceCommerceProductModel);
            this._$salesforceCommerceElement.addClass("placeholder-preview");
            this._$salesforceCommerceElement.appendTo(element);
        },

        /**
         * Create an instance of a <tt>SalesforceCommerceWidgetPreview</tt>.
         *
         * @param {SalesforceCommerceProductModel} salesforceCommerceProductModel
         * @return {jQuery} The jQuery element for the created widget.
         * @private
         */
        _createWidgetContainer: function(salesforceCommerceProductModel) {
            var salesforceCommerceWidgetPreview = new SalesforceCommerceWidgetPreview({
                model: salesforceCommerceProductModel
            });

            salesforceCommerceWidgetPreview.render();

            return salesforceCommerceWidgetPreview.$el;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/view/TransformableSalesforceCommerceWidget.js